import { CalendarIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  InputGroup,
  InputRightElement,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import axios from "axios";
import React from "react";
import { auth } from "../../../../core/auth";
import { env } from "../../../../env";
import DataTable from "../../../../shared/components/DataTable/DataTable";
import useQueryDataTable from "../../../../shared/components/DataTable/useQueryDataTable";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import { useBankedMinutesAiCapabilities } from "./bankedMinutes.ai";
import ContractTypeSelect from "../../../../shared/components/ContractTypeSelect";
import { createFilters } from "../../../../shared/hooks/useFilters";
import useApi from "../../../../shared/hooks/useApi";
import { usePageFetchingObserver } from "../../../../shared/hooks/usePageIndication";
import { ContractTypeId, OfficeId } from "../../../../shared/schema/schema";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";
import { EntitySelect } from "../../../workflow/components/EntityFormControl";
import { LocalDate } from "@js-joda/core";

type BankedMinutesVisit = {
  visitInstanceId: number;
  patientId: number;
  patientDisplayId: number | null;
  patientFirstName: string;
  patientLastName: string;
  visitDate: string;
  adjustmentMinutes: number;
  fullyAuthorized: boolean;
  contractName: string;
  officeName: string;
};

type FilterState = {
  from?: string;
  to?: string;
  officeIds?: OfficeId[];
  contractTypeIds?: ContractTypeId[];
};

const initialFilterState: FilterState = {
  from: LocalDate.of(LocalDate.now().year(), LocalDate.now().monthValue(), 1).toString(),
  to: LocalDate.of(
    LocalDate.now().year(),
    LocalDate.now().monthValue(),
    LocalDate.now().lengthOfMonth()
  ).toString(),
};

const { createRangeDatePickerFilter } = createFilters<FilterState>();

export default function BankedMinutesViewTab() {
  const [filters, setFilters] = React.useState<FilterState>(initialFilterState);

  // This tab tells the omnibar what its own filters are, while it is on screen.
  useBankedMinutesAiCapabilities({
    setFilter: (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
  });
  const { queries } = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();

  const queryParams = {
    from: filters.from,
    to: filters.to,
    officeIds: filters.officeIds,
    contractTypeIds: filters.contractTypeIds,
  };

  const queryOptions = queries.bankedMinutes.visits(queryParams);

  const fromToFilter = createRangeDatePickerFilter({
    label: "Date range",
    startDate: {
      name: "from",
      value: filters.from ? LocalDate.parse(filters.from) : null,
    },
    endDate: {
      name: "to",
      value: filters.to ? LocalDate.parse(filters.to) : null,
    },
    onChange: (key, value) => setFilters((prev) => ({ ...prev, [key]: value?.toString() })),
  });

  const [selection, setSelection] = React.useState(new Set<number>());

  const query = useQuery({
    ...queryOptions,
    select: (data: { list: BankedMinutesVisit[] }) => data.list,
    enabled: false,
  });

  const resetMutation = useMutation({
    mutationFn: async (visitInstanceIds: number[]) => {
      const authState = auth.getAuthOrFail();
      await axios.post(
        `${env.API_URL}agencies/${authState.data.agency.id}/agency_members/${authState.data.agencyMember.id}/banked_minutes/jobs/reset`,
        { visitInstanceIds },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${authState.tokens.authToken}`,
          },
        }
      );
    },
    onSuccess: () => {
      toast({
        title: "Reset job started successfully",
        status: "success",
        position: "top-right",
      });
      setSelection(new Set());
      query.refetch();
      queryClient.invalidateQueries({ queryKey: ["banked_minutes", "jobs"] });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not start reset job",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (file: File) => {
      const authState = auth.getAuthOrFail();
      const data = new FormData();
      data.append("file", file, file.name);
      await axios.post(
        `${env.API_URL}agencies/${authState.data.agency.id}/agency_members/${authState.data.agencyMember.id}/banked_minutes/jobs/update`,
        data,
        {
          headers: {
            Authorization: `Token ${authState.tokens.authToken}`,
          },
        }
      );
    },
    onSuccess: () => {
      toast({
        title: "Update job started successfully",
        status: "success",
        position: "top-right",
      });
      queryClient.invalidateQueries({ queryKey: ["banked_minutes", "jobs"] });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not start update job",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  function handleReset() {
    if (selection.size > 0) {
      resetMutation.mutate(Array.from(selection));
    }
  }

  function handleUpdate() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.xlsx";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        updateMutation.mutate(file);
      }
    };
    input.click();
  }

  const columns = React.useMemo(() => {
    return createColumns(
      selection,
      (id) =>
        setSelection((prev) => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          return next;
        }),
      () => {
        const allToggled = selection.size === query.data?.length;
        setSelection(
          new Set(allToggled ? [] : query.data?.map((row) => row.visitInstanceId) ?? [])
        );
      },
      selection.size === query.data?.length
    );
  }, [query.data, selection]);

  const { dataTableProps } = useQueryDataTable({
    query,
    columns,
    columnVisiblity: {},
  });

  usePageFetchingObserver(queryOptions);

  const actionNode = (
    <>
      <Button
        colorScheme="blue"
        isDisabled={query.isFetching}
        leftIcon={<SearchIcon />}
        onClick={() => query.refetch()}
      >
        Search
      </Button>
      <Button
        colorScheme="red"
        isDisabled={query.isFetching || selection.size === 0 || resetMutation.isPending}
        onClick={handleReset}
      >
        Reset
      </Button>
      <Button colorScheme="green" isDisabled={updateMutation.isPending} onClick={handleUpdate}>
        Update
      </Button>
    </>
  );

  const filterNode = (
    <>
      <InputGroup width="xs">
        <RangeDatePicker {...fromToFilter} inputProps={{ width: "full" }} />
        <InputRightElement>
          <CalendarIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
        </InputRightElement>
      </InputGroup>

      <EntitySelect
        input={{ type: "entity", entity: "Office" }}
        label="Office"
        multiple={true}
        renderUnselected="Office"
        value={filters.officeIds ?? null}
        onChange={(x) => setFilters((prev) => ({ ...prev, officeIds: x ?? undefined }))}
      />

      <ContractTypeSelect
        label="Contract"
        multiple={true}
        value={filters.contractTypeIds ?? null}
        onChange={(x) => setFilters((prev) => ({ ...prev, contractTypeIds: x ?? undefined }))}
      />
    </>
  );

  return (
    <Box>
      <DataTable
        {...dataTableProps}
        actionNode={actionNode}
        filterNode={filterNode}
        spacing="tight"
      />
    </Box>
  );
}

const { accessor, display } = createColumnHelper<BankedMinutesVisit>();

const createColumns = (
  selected: Set<number>,
  onSelect: (id: number) => void,
  onSelectAll: () => void,
  areAllSelected: boolean
) => [
  display({
    id: "_selected",
    cell: (x) => {
      return (
        <Checkbox
          isChecked={selected.has(x.row.original.visitInstanceId)}
          size="lg"
          onChange={() => onSelect(x.row.original.visitInstanceId)}
        />
      );
    },
    header: () => {
      return (
        <Flex align="center" gap={2}>
          <Checkbox isChecked={areAllSelected} size="lg" onChange={() => onSelectAll()} />
          <FormLabel>Select All</FormLabel>
        </Flex>
      );
    },
    meta: {
      sticky: "left",
    },
  }),
  accessor("patientDisplayId", {
    header: "Patient Display ID",
    sortingFn: "alphanumeric",
  }),
  accessor("patientFirstName", {
    cell: (x) => `${x.row.original.patientFirstName} ${x.row.original.patientLastName}`,
    header: "Patient Name",
    sortingFn: "alphanumeric",
  }),
  accessor("visitDate", {
    header: "Visit Date",
    sortingFn: "alphanumeric",
  }),
  accessor("adjustmentMinutes", {
    header: "Adjustment Minutes",
    sortingFn: "basic",
  }),
  accessor("contractName", {
    header: "Contract",
    sortingFn: "alphanumeric",
  }),
  accessor("officeName", {
    header: "Office",
    sortingFn: "alphanumeric",
  }),
  accessor("fullyAuthorized", {
    cell: (x) => (x.row.original.fullyAuthorized ? "Yes" : "No"),
    header: "Fully Authorized",
    sortingFn: "basic",
  }),
];
